import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";

import API_URL from "../../api/API";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";

import LocationForm from "../businesslocations/LocationForm";

const WaiverLocationsEditor = () => {

    const { business } = useBusinessAuthContext();

    const {
        waiver,refreshChangeSummary,setSaveHandler,setHasUnsavedChanges,
    } = useOutletContext();

    const waiverId = waiver?._id;

    const [locations, setLocations] = useState([]);

    const [selected, setSelected] = useState([]);

    const [blockedLocations, setBlockedLocations] = useState([]);

    const [loading, setLoading] = useState(true);

    const [saving, setSaving] = useState(false);

    const [isDirty, setIsDirty] = useState(false);

    const [showCreateForm, setShowCreateForm] = useState(false);

    
    /*
    ----------------------------------------------------------
    Save Draft
    ----------------------------------------------------------
    */
    const saveDraft = useCallback(async () => { ////////NEW 6th July 2026

        if (saving) return false;

        if (!Array.isArray(selected) || selected.length === 0) {
            return false;
        }

        setSaving(true);

        try {

            const res = await fetch(

                `${API_URL.BUSINESS_WAIVER_SMART_STEP_SAVE_LOCATION}/${waiverId}`,

                {

                    method: "PATCH",

                    headers: {

                        Authorization: `Bearer ${business.token}`,

                        "Content-Type": "application/json",

                        "x-session-id": business.sessionId,

                        "x-frontend-url": window.location.pathname,

                    },

                    body: JSON.stringify({

                        locationIds: selected,

                    }),

                }

            );

            const data = await res.json();

            if (!data.success) {

                alert(data.message);

                return false;

            }

            await refreshChangeSummary(); // refreshWaiver() must not be used, because it reloads changes overwritten here.
            
            return true;

        }
        catch (err) {

            console.error(err);

            return false;

        }
        finally {

            setSaving(false);

        }

    }, [
        waiverId,
        selected,
        business,
        refreshChangeSummary,
        saving,
    ]); /* Otherwise it will will still recreate saveHandler every render unless saveDraft is stable. */
    

    /* Each tab registers its save function*/  ////////NEW 6th July 2026
    useEffect(() => {
        setSaveHandler(() => saveDraft);
        return () => setSaveHandler(null);
    }, []); /* STOP tracking saveDraft in dependencies - else infintie rendering loop in browser */ 
    
    useEffect(() => {
        setHasUnsavedChanges(isDirty);
    }, [isDirty]); /* setHasUnsavedChanges DO NOT include setter in deps (it’s stable anyway). */
   
    
    /*
    ----------------------------------------------------------
    Load Locations
    ----------------------------------------------------------
    */

    useEffect(() => {

        if (!waiver || !business) return;

        const loadData = async () => {

            try {

                setLoading(true);

                const headers = {

                    headers: {

                        Authorization: `Bearer ${business.token}`,

                        "x-session-id": business.sessionId,

                        "x-frontend-url": window.location.pathname,

                    },

                };

                const isArchivedWaiver =

                    waiver.isDeleted ||

                    waiver.waiverStatus === "archived_by_admin" ||

                    waiver.waiverStatus === "archived_by_new_version" ||

                    waiver.waiverStatus === "archived_by_location_deletion";

                const [locRes, conflictRes] = await Promise.all([

                    fetch(

                        `${API_URL.BUSINESS_LOCATIONS}${
                            isArchivedWaiver
                                ? "?includeArchived=true"
                                : ""
                        }`,

                        headers

                    ),

                    fetch(

                        `${API_URL.EXISTING_LOCATIONS}/${waiver.serviceId}/${waiverId}`,

                        headers

                    ),

                ]);

                const locData = await locRes.json();

                const conflictData = await conflictRes.json();

                if (!locData.success) return;

                setBlockedLocations(

                    conflictData.usedLocationIds || []

                );

                const waiverLocationIds = new Set(

                    (waiver.locations || []).map(

                        loc => String(loc._id)

                    )

                );

                const filteredLocations =

                    locData.locations.filter(loc => {

                        const archived =

                            loc.isDeleted ||

                            !loc.isActive;

                        if (!archived) return true;

                        return waiverLocationIds.has(

                            String(loc._id)

                        );

                    });

                setLocations(filteredLocations);

                setSelected(

                    (waiver.locations || []).map(

                        loc => String(loc._id)

                    )

                );

            }
            catch (err) {

                console.error(err);

            }
            finally {

                setLoading(false);

            }

        };

        loadData();

    }, [waiver, business]);

    /*
    ----------------------------------------------------------
    Toggle Location
    ----------------------------------------------------------
    */

    const toggleLocation = id => {

        setSelected(prev => {

            const updated = prev.includes(id)

                ? prev.filter(x => x !== id)

                : [...prev, id];

            return updated;

        });

        setIsDirty(true);

    };

    /*
    ----------------------------------------------------------
    Auto Save

    Future Improvement

    This editor may eventually use a dedicated
    "Save Changes" button instead of auto-save.
    ----------------------------------------------------------
    */

    useEffect(() => {

        if (!isDirty) return;

        const timer = setTimeout(() => {

            saveDraft();

            setIsDirty(false);
            setHasUnsavedChanges(false); ////////NEW 6th July 2026

        }, 1500);

        return () => clearTimeout(timer);

    }, [selected, isDirty]);


    /*
    ----------------------------------------------------------
    Location Created
    ----------------------------------------------------------
    */

    const handleLocationCreated = location => {

        setLocations(prev => [

            ...prev,

            location,

        ]);

        setSelected(prev => [

            ...prev,

            location._id,

        ]);

        setShowCreateForm(false);

        setIsDirty(true);

    };

        /*
    ----------------------------------------------------------
    Render
    ----------------------------------------------------------
    */

    if (loading) {

        return <div>Loading...</div>;

    }

    return (

        <div className="max-w-3xl">

            <div className="bg-white rounded-xl shadow-sm p-8">

                <div className="flex items-start justify-between mb-6">

                    <div>

                        <h2 className="text-lg font-semibold">

                            Waiver Locations

                        </h2>

                        <p className="text-sm text-muted-foreground mt-1">

                            Choose which locations will use this waiver.

                        </p>

                    </div>

                    {saving && (

                        <div className="text-sm text-gray-500">

                            Saving...

                        </div>

                    )}

                </div>

                {/* --------------------------------------------------
                    Empty State
                -------------------------------------------------- */}

                {locations.length === 0 ? (

                    <div className="border rounded-lg p-8 text-center">

                        <h3 className="font-medium mb-2">

                            No locations found

                        </h3>

                        <p className="text-sm text-muted-foreground mb-6">

                            Create your first location to begin assigning
                            this waiver.

                        </p>

                        {!showCreateForm && (

                            <button

                                type="button"

                                onClick={() =>
                                    setShowCreateForm(true)
                                }

                                className="px-5 py-2 rounded-full bg-yellow-500 hover:bg-yellow-600"

                            >

                                Create Location

                            </button>

                        )}

                    </div>

                ) : (

                    <>

                        {/* ------------------------------------------
                            Location List
                        ------------------------------------------ */}

                        <div className="border rounded-lg divide-y">

                            {locations.map(location => {

                                const disabled =

                                    blockedLocations.includes(location._id) ||

                                    location.isDeleted ||

                                    !location.isActive;

                                return (

                                    <label

                                        key={location._id}

                                        className={`flex gap-4 p-4 ${
                                            disabled
                                                ? "opacity-60"
                                                : "cursor-pointer"
                                        }`}

                                    >

                                        <input

                                            type="checkbox"

                                            disabled={disabled}

                                            checked={selected.includes(location._id)}

                                            onChange={() =>
                                                toggleLocation(location._id)
                                            }

                                            className="mt-1"

                                        />

                                        <div className="flex-1">

                                            <div className="font-medium">

                                                {location.name}

                                            </div>

                                            <div className="text-sm text-muted-foreground">

                                                {location.street}

                                            </div>

                                            <div className="text-sm text-muted-foreground">

                                                {location.city},{" "}
                                                {location.state}{" "}
                                                {location.zip}

                                                {(location.isDeleted ||

                                                    !location.isActive) &&

                                                    " (Archived)"}

                                            </div>

                                            {blockedLocations.includes(

                                                location._id

                                            ) && (

                                                <div className="text-xs text-red-500 mt-2">

                                                    A published or draft
                                                    waiver already exists
                                                    for this service at
                                                    this location.

                                                </div>

                                            )}

                                        </div>

                                    </label>

                                );

                            })}

                        </div>

                        {/* ------------------------------------------
                            Actions
                        ------------------------------------------ */}

                        {!showCreateForm && (

                            <button

                                type="button"

                                onClick={() =>
                                    setShowCreateForm(true)
                                }

                                className="mt-5 text-sm underline"

                            >

                                + Add another location

                            </button>

                        )}

                    </>

                )}

                {/* --------------------------------------------------
                    Inline Create Form
                -------------------------------------------------- */}

                {showCreateForm && (

                    <div className="mt-8 border-t pt-8">

                        <div className="flex items-center justify-between mb-5">

                            <h3 className="font-semibold">

                                Create New Location

                            </h3>

                            <button

                                type="button"

                                onClick={() =>
                                    setShowCreateForm(false)
                                }

                                className="text-sm text-muted-foreground"

                            >

                                Cancel

                            </button>

                        </div>

                        <LocationForm
                            embedded 
                            onSuccess={handleLocationCreated}

                        />

                    </div>

                )}

            </div>

        </div>

    );

};

export default WaiverLocationsEditor;